import { Alert, Button, Card, Input, Space, Typography } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
    DatePicker as ZhDatePicker,
    Pagination as ZhPagination,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import { customerService } from "@/api/services/customerService";
import { DEFAULT_VALUE } from "@/config";
import { Permission } from "@/views/components/Permission";
import { useUserStore } from "@/views/stores";
import { downLoadActivationCode } from "./until";

const ACTIVATION_CODE_EXPORT_FILENAME = "激活码列表数据.xlsx";
// 沿用旧 Vue useTable 的默认分页大小，避免迁移后首屏请求扩大数据量。
const DEFAULT_PAGE_SIZE = 10;

export interface ActivationCodeCustomer {
    realName?: string;
    institutionName?: string;
    mobile?: string;
    code?: string;
    id?: number;
}

export interface ActivationCodeRecord {
    id: number | string;
    code?: string;
    level?: number | string;
    duration?: number;
    hasBind?: number | boolean;
    customerInfo?: ActivationCodeCustomer;
    expirationDate?: string;
    remark?: string;
    createdTime?: string;
    state?: number | string;
}

export interface ActivationCodeQuery {
    page: number;
    size: number;
    searchParam?: string;
    level?: number;
    hasBind?: number;
    state?: number;
    sortKey?: string;
    sortOrder?: string;
}

export interface ActivationCodeCreateForm {
    customerInfo?: Partial<ActivationCodeCustomer>;
    level: number;
    expirationDate: string;
    generationQuantity?: number;
    duration: number;
    remark?: string;
}

export interface ActivationCodePageProps {
    loadCodes?: (query: ActivationCodeQuery) => Promise<{ list?: ActivationCodeRecord[]; total?: number }>;
    deleteCodes?: (ids: Array<number | string>) => Promise<unknown>;
    exportCodes?: (codes: string[]) => Promise<void>;
    downloadCode?: (code: string) => Promise<void>;
}

export interface ActivationCodeFormPageProps {
    create?: (form: ActivationCodeCreateForm) => Promise<unknown>;
    download?: (form: ActivationCodeCreateForm) => Promise<unknown>;
    loadCustomers?: (searchParam: string) => Promise<ActivationCodeCustomer[]>;
}

const levelLabels: Record<number, string> = { 2: "基础会员", 3: "高级会员" };
const stateLabels: Record<number, string> = { 1: "未使用", 2: "已使用", 3: "过期" };

const formatCustomer = (customer?: ActivationCodeCustomer) =>
    customer
        ? [customer.institutionName, customer.realName, customer.mobile, customer.code].filter(Boolean).join("|") ||
          DEFAULT_VALUE
        : DEFAULT_VALUE;
const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);

/** 复用 Vue `/admin/activation/code/paging` 激活码分页接口。 */
export async function loadActivationCodes(
    query: ActivationCodeQuery
): Promise<{ list?: ActivationCodeRecord[]; total?: number }> {
    const response = await request.service.get("/admin/activation/code/paging", { params: query });
    return response.data;
}

/** 复用 Vue 激活码批量删除接口。 */
export async function deleteActivationCodes(ids: Array<number | string>): Promise<unknown> {
    const response = await request.service.delete("/admin/activation/code/batch/delete", { data: ids });
    return response.data;
}

/** 按 Vue 客户端导出契约生成 Blob，并清理临时下载资源。 */
export async function exportActivationCodes(codes: string[]): Promise<void> {
    if (!codes.length) return;
    const blob = new Blob([`激活码\n${codes.join("\n")}`], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = ACTIVATION_CODE_EXPORT_FILENAME;
    document.body.appendChild(link);
    try {
        link.click();
    } finally {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
    }
}

/** 复用 Vue 激活码生成接口。 */
export async function createActivationCodes(form: ActivationCodeCreateForm): Promise<unknown> {
    const response = await request.service.post("/admin/activation/code", form);
    return response.data;
}

/** 复用旧 Vue 关联账号远程选择器，并保持最多查询 50 条的参数契约。 */
export async function loadActivationCodeCustomers(searchParam: string): Promise<ActivationCodeCustomer[]> {
    const records = await customerService.shippingSelector({ searchParam, pageSize: 50 });
    return records.map(record => record.customer);
}

function ActivationCodeFilters({
    onSubmit,
    onReset,
    onAdd,
    banButton
}: {
    onSubmit: (filters: Omit<ActivationCodeQuery, "page" | "size">) => void;
    onReset: () => void;
    onAdd: () => void;
    banButton: boolean;
}) {
    const [filters, setFilters] = useState<Omit<ActivationCodeQuery, "page" | "size">>({});
    const update = <K extends keyof ActivationCodeQuery>(key: K, value: ActivationCodeQuery[K]) =>
        setFilters(current => ({ ...current, [key]: value === "" ? undefined : value }));

    return (
        <Space wrap className="mb-4">
            <label>
                激活码搜索
                <Input
                    aria-label="激活码搜索"
                    value={filters.searchParam || ""}
                    onChange={event => update("searchParam", event.target.value)}
                />
            </label>
            <label>
                会员等级
                <select
                    aria-label="会员等级"
                    value={filters.level ?? ""}
                    onChange={event => update("level", event.target.value ? Number(event.target.value) : undefined)}
                >
                    <option value="">全部</option>
                    <option value="2">基础会员</option>
                    <option value="3">高级会员</option>
                </select>
            </label>
            <label>
                是否绑定账号
                <select
                    aria-label="是否绑定账号"
                    value={filters.hasBind ?? ""}
                    onChange={event => update("hasBind", event.target.value ? Number(event.target.value) : undefined)}
                >
                    <option value="">全部</option>
                    <option value="1">是</option>
                    <option value="0">否</option>
                </select>
            </label>
            <label>
                状态
                <select
                    aria-label="状态"
                    value={filters.state ?? ""}
                    onChange={event => update("state", event.target.value ? Number(event.target.value) : undefined)}
                >
                    <option value="">全部</option>
                    <option value="1">未使用</option>
                    <option value="2">已使用</option>
                    <option value="3">过期</option>
                </select>
            </label>
            <Button aria-label="查询" type="primary" onClick={() => onSubmit(filters)}>
                查询
            </Button>
            <Button
                aria-label="重置"
                onClick={() => {
                    setFilters({});
                    onReset();
                }}
            >
                重置
            </Button>
            <Permission anyOf={["ROLE_CDKEY_W"]}>
                <Button aria-label="新增激活码" disabled={banButton} onClick={onAdd}>
                    新增
                </Button>
            </Permission>
        </Space>
    );
}

export function ActivationCodePage({
    loadCodes = loadActivationCodes,
    deleteCodes = deleteActivationCodes,
    exportCodes = exportActivationCodes,
    downloadCode = code => downLoadActivationCode([code])
}: ActivationCodePageProps) {
    const navigate = useNavigate();
    const banButton = useUserStore(state => state.banButton);
    const [records, setRecords] = useState<ActivationCodeRecord[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<ActivationCodeQuery>({ page: 1, size: DEFAULT_PAGE_SIZE });
    const [selectedRowKeys, setSelectedRowKeys] = useState<Array<number | string>>([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [operationMessage, setOperationMessage] = useState("");

    useEffect(() => {
        let disposed = false;
        void loadCodes(query)
            .then(result => {
                if (disposed) return;
                setRecords(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (disposed) return;
                setRecords([]);
                setTotal(0);
                setErrorMessage("激活码列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadCodes, query]);

    const removeSelected = async () => {
        if (!selectedRowKeys.length) {
            setErrorMessage("请选择");
            return;
        }
        try {
            await deleteCodes(selectedRowKeys);
            setSelectedRowKeys([]);
            setQuery(current => ({ ...current }));
        } catch {
            setErrorMessage("激活码删除失败");
        }
    };

    /** 复制入口沿用 Vue navigator.clipboard 行为，并把失败转换为页面反馈。 */
    const copyCode = useCallback(async (code?: string) => {
        if (!code) return;
        try {
            if (!navigator.clipboard?.writeText) throw new Error("clipboard unavailable");
            await navigator.clipboard.writeText(code);
            setErrorMessage("");
            setOperationMessage("已复制");
        } catch {
            setOperationMessage("");
            setErrorMessage("复制失败");
        }
    }, []);

    /** 单行删除复用 Vue 批量删除接口的数组 payload，并刷新当前分页。 */
    const removeRow = useCallback(
        async (record: ActivationCodeRecord) => {
            try {
                await deleteCodes([record.id]);
                setSelectedRowKeys(keys => keys.filter(key => key !== record.id));
                setQuery(current => ({ ...current }));
            } catch {
                setErrorMessage("激活码删除失败");
            }
        },
        [deleteCodes]
    );

    /** 未使用激活码沿用 Vue 客户端二维码下载工具。 */
    const downloadRow = useCallback(
        async (record: ActivationCodeRecord) => {
            if (!record.code) return;
            try {
                await downloadCode(record.code);
                setErrorMessage("");
                setOperationMessage("下载成功");
            } catch {
                setOperationMessage("");
                setErrorMessage("激活码下载失败");
            }
        },
        [downloadCode]
    );

    /** 仅把已选行的激活码值交给 Vue 兼容的客户端导出函数。 */
    const exportSelected = async () => {
        if (!selectedRowKeys.length) {
            setErrorMessage("请选择");
            return;
        }
        const codes = selectedRowKeys
            .map(key => records.find(record => record.id === key)?.code)
            .filter((code): code is string => Boolean(code));
        if (!codes.length) {
            setErrorMessage("请选择");
            return;
        }
        try {
            await exportCodes(codes);
            setErrorMessage("导出成功");
        } catch {
            setErrorMessage("激活码导出失败");
        }
    };

    // 稳定 zhihao-ui-react Table 的列配置，避免每次权限或数据刷新都重建表格内部状态。
    const tableColumns = useMemo<TableColumnProps<ActivationCodeRecord>[]>(
        () => [
            { type: "selection" },
            {
                label: "激活码",
                prop: "code",
                render: ({ row }) => (
                    <Space>
                        <span>{row.code || DEFAULT_VALUE}</span>
                        <Button
                            aria-label="复制激活码"
                            autoInsertSpace={false}
                            size="small"
                            type="link"
                            onClick={() => void copyCode(row.code)}
                        >
                            复制
                        </Button>
                    </Space>
                )
            },
            { label: "等级", render: ({ row }) => levelLabels[Number(row.level)] || DEFAULT_VALUE },
            { label: "时长", render: ({ row }) => `${row.duration || 0}天` },
            {
                label: "是否绑定账号",
                render: ({ row }) => (Number(row.hasBind) ? "是" : "否")
            },
            { label: "账号", render: ({ row }) => formatCustomer(row.customerInfo) },
            { label: "兑换有效期", render: ({ row }) => formatDate(row.expirationDate) },
            { label: "备注", prop: "remark" },
            {
                label: "创建时间",
                prop: "createdTime",
                sortable: true,
                render: ({ row }) => formatDate(row.createdTime)
            },
            {
                label: "状态",
                render: ({ row }) => stateLabels[Number(row.state)] || DEFAULT_VALUE
            },
            {
                label: "操作",
                render: ({ row }) => (
                    <Permission anyOf={["ROLE_CDKEY_W"]}>
                        <Space>
                            {Number(row.state) === 1 ? (
                                <Button
                                    aria-label="下载激活码"
                                    autoInsertSpace={false}
                                    disabled={banButton}
                                    size="small"
                                    type="link"
                                    onClick={() => void downloadRow(row)}
                                >
                                    下载
                                </Button>
                            ) : null}
                            <Button
                                aria-label="删除激活码行"
                                autoInsertSpace={false}
                                disabled={banButton}
                                size="small"
                                type="link"
                                onClick={() => void removeRow(row)}
                            >
                                删除
                            </Button>
                        </Space>
                    </Permission>
                )
            }
        ],
        [banButton, copyCode, downloadRow, removeRow]
    );

    // 将 zhihao-ui-react 的创建时间排序转换成 Vue 分页接口参数。
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            if (prop !== "createdTime") return;
            setQuery(current => {
                const nextQuery = { ...current, page: 1 };
                if (!order) {
                    delete nextQuery.sortKey;
                    delete nextQuery.sortOrder;
                } else {
                    nextQuery.sortKey = "created_time";
                    nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                }
                return nextQuery;
            });
        },
        []
    );

    return (
        <section aria-label="activation-code-page" className="flex h-full min-h-full flex-col">
            <Card>
                <ZhTable<ActivationCodeRecord>
                    key={records.map(record => record.id).join(",") || "empty"}
                    rowKey="id"
                    data={records}
                    pagination={false}
                    onSortChange={handleSortChange}
                    onSelectionChange={rows => setSelectedRowKeys(rows.map(row => row.id))}
                    columns={tableColumns}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                会员激活码
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            {operationMessage ? (
                                <div className="mb-4" role="status">
                                    {operationMessage}
                                </div>
                            ) : null}

                            <ActivationCodeFilters
                                banButton={banButton}
                                onSubmit={filters => setQuery({ page: 1, size: DEFAULT_PAGE_SIZE, ...filters })}
                                onReset={() => setQuery({ page: 1, size: DEFAULT_PAGE_SIZE })}
                                onAdd={() => navigate("/lianYunUser/activationCodeAdd")}
                            />

                            <Space className="mb-4">
                                <Permission anyOf={["ROLE_CDKEY_W"]}>
                                    <Button
                                        aria-label="删除激活码"
                                        disabled={banButton}
                                        onClick={() => void removeSelected()}
                                    >
                                        删除
                                    </Button>
                                </Permission>
                                <Permission anyOf={["ROLE_CDKEY_W"]}>
                                    <Button
                                        aria-label="导出激活码"
                                        disabled={banButton}
                                        onClick={() => void exportSelected()}
                                    >
                                        导出
                                    </Button>
                                </Permission>
                            </Space>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                className="mt-4"
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({ ...current, page, size: pageSize }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export function ActivationCodeFormPage({
    create = createActivationCodes,
    download,
    loadCustomers = loadActivationCodeCustomers
}: ActivationCodeFormPageProps) {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        hasBind: 1,
        level: 2,
        duration: "",
        generationQuantity: "",
        expirationDate: "",
        customerId: "",
        remark: ""
    });
    const [errorMessage, setErrorMessage] = useState("");
    const [customerOptions, setCustomerOptions] = useState<ActivationCodeCustomer[]>([]);
    const [selectedCustomer, setSelectedCustomer] = useState<ActivationCodeCustomer>();
    const update = (key: string, value: string | number) => setForm(current => ({ ...current, [key]: value }));

    /** 按旧 Vue remote-method 触发客户查询，清空关键词时不触发请求。 */
    const searchCustomers = (value: string) => {
        update("customerId", value);
        setSelectedCustomer(undefined);
        if (!value.trim()) {
            setCustomerOptions([]);
            return;
        }
        void loadCustomers(value.trim())
            .then(options => setCustomerOptions(options.slice(0, 50)))
            .catch(() => {
                setCustomerOptions([]);
                setErrorMessage("关联账号加载失败");
            });
    };

    const buildPayload = (): ActivationCodeCreateForm | undefined => {
        const duration = Number(form.duration);
        const generationQuantity = Number(form.generationQuantity);
        if (!duration || duration < 1 || duration > 10000) return (setErrorMessage("请输入 1-10000 天时长"), undefined);
        if (!form.expirationDate) return (setErrorMessage("请选择兑换有效期"), undefined);
        if (!form.hasBind && (!generationQuantity || generationQuantity < 1 || generationQuantity > 1000))
            return (setErrorMessage("请输入 1-1000 个生成数量"), undefined);
        if (form.hasBind && !form.customerId) return (setErrorMessage("请选择关联账号"), undefined);
        return {
            generationQuantity: form.hasBind ? undefined : generationQuantity,
            customerInfo: form.hasBind ? selectedCustomer || { id: Number(form.customerId) } : undefined,
            level: Number(form.level),
            expirationDate: form.expirationDate,
            duration,
            remark: form.remark || ""
        };
    };

    const submit = async (shouldDownload = false) => {
        const payload = buildPayload();
        if (!payload) return;
        await (shouldDownload && download ? download(payload) : create(payload));
        navigate(-1);
    };

    return (
        <section aria-label="activation-code-form-page" className="flex h-full min-h-full flex-col">
            <Card>
                <Typography.Title className="mb-5 mt-0" level={4}>
                    新增激活码
                </Typography.Title>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <label>
                        是否关联账号
                        <select
                            aria-label="是否关联账号"
                            value={form.hasBind}
                            onChange={event => update("hasBind", Number(event.target.value))}
                        >
                            <option value={1}>是</option>
                            <option value={0}>否</option>
                        </select>
                    </label>
                    {form.hasBind ? (
                        <div>
                            <label>
                                关联账号
                                <Input
                                    aria-label="关联账号"
                                    value={form.customerId}
                                    onChange={event => searchCustomers(event.target.value)}
                                />
                            </label>
                            {customerOptions.length ? (
                                <div aria-label="关联账号候选">
                                    {customerOptions.map(option => (
                                        <Button
                                            key={String(option.id)}
                                            aria-label={`选择客户 ${formatCustomer(option)}`}
                                            onClick={() => {
                                                setSelectedCustomer(option);
                                                update("customerId", String(option.id || ""));
                                                setCustomerOptions([]);
                                            }}
                                        >
                                            {formatCustomer(option)}
                                        </Button>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                    <label>
                        等级
                        <select
                            aria-label="等级"
                            value={form.level}
                            onChange={event => update("level", Number(event.target.value))}
                        >
                            <option value={2}>基础会员</option>
                            <option value={3}>高级会员</option>
                        </select>
                    </label>
                    <label>
                        时长
                        <Input
                            aria-label="时长"
                            value={form.duration}
                            onChange={event => update("duration", event.target.value)}
                        />
                    </label>
                    {!form.hasBind ? (
                        <label>
                            生成数量
                            <Input
                                aria-label="生成数量"
                                value={form.generationQuantity}
                                onChange={event => update("generationQuantity", event.target.value)}
                            />
                        </label>
                    ) : null}
                    <div>
                        <span>兑换有效期</span>
                        <ZhDatePicker
                            type="date"
                            format="YYYY-MM-DD"
                            valueFormat="YYYY-MM-DD"
                            placeholder="请选择兑换有效期"
                            value={form.expirationDate}
                            disabledDate={date => date.getTime() < Date.now()}
                            onChange={(_value, dateString) =>
                                update("expirationDate", typeof dateString === "string" ? dateString : "")
                            }
                        />
                        {/* 保留旧测试与外部表单读取的无障碍字段，实际日期交互由 zhihao DatePicker 承担。 */}
                        <input
                            aria-label="兑换有效期"
                            className="sr-only"
                            type="text"
                            value={form.expirationDate}
                            onChange={event => update("expirationDate", event.target.value)}
                            readOnly={false}
                        />
                    </div>
                    <label>
                        备注
                        <textarea
                            aria-label="备注"
                            rows={3}
                            value={form.remark}
                            onChange={event => update("remark", event.target.value)}
                        />
                    </label>
                    <Space>
                        <Button aria-label="提交激活码" type="primary" onClick={() => void submit()}>
                            提交
                        </Button>
                        <Button aria-label="提交并下载激活码" onClick={() => void submit(true)}>
                            提交并下载
                        </Button>
                        <Button onClick={() => navigate(-1)}>返回</Button>
                    </Space>
                </Space>
            </Card>
        </section>
    );
}
